/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { useCallback, useMemo, useState } from "react";

import { usePlayerComponents } from "../../../../../../shared/context/PlayerComponentsContext";
import { usePlayerTranslation } from "../../../../../../shared/context/PlayerTranslationContext";
import MuteIcon from "../../../../../../shared/icons/player/Sound_off.svg";
import VolumeHighIcon from "../../../../../../shared/icons/player/Sound_on_high.svg";
import VolumeLowIcon from "../../../../../../shared/icons/player/Sound_on_low.svg";
import { usePlayerVolume } from "../../../../../Controller";
import { useOverlayState } from "../../../../../Overlay";

import "./VolumeControl.scss";

export const VolumeControl = () => {
  const { volume, isMuted, toggleMute, changeVolume } = usePlayerVolume();
  const [focused, setFocused] = useState<boolean>(false);
  const { Button, Slider } = usePlayerComponents();
  const { t } = usePlayerTranslation();

  const { toggleSettings } = useOverlayState();

  const onMouseEnter = useCallback(() => {
    toggleSettings(true);
    setFocused(true);
  }, [toggleSettings]);

  const onMouseLeave = useCallback(() => {
    toggleSettings(false);
    setFocused(false);
  }, [toggleSettings]);

  const icon = useMemo(() => {
    if (isMuted) {
      return <MuteIcon aria-hidden="true" focusable="false" />;
    }

    return volume >= 0.5 ? (
      <VolumeHighIcon aria-hidden="true" focusable="false" />
    ) : (
      <VolumeLowIcon aria-hidden="true" focusable="false" />
    );
  }, [isMuted, volume]);

  const buttonLabel = t(
    isMuted ? "PLAYER_TOOLTIP_UNMUTE" : "PLAYER_TOOLTIP_MUTE",
  );
  const sliderLabel = t("COMMON__BUTTON_VOLUME");

  return (
    <div className="VolumeControl">
      <Button
        variant="secondary"
        onClick={toggleMute}
        forceHoverStyle={focused}
        aria-label={buttonLabel}
        iconOnly
      >
        {icon}
      </Button>
      <div
        className="VolumeControl__ToggleArea"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="VolumeControl__ToggleArea__Panel">
          <Slider
            value={volume}
            onChange={changeVolume}
            min={0}
            max={1}
            step={0.01}
            vertical
            aria-label={sliderLabel}
          />
        </div>
      </div>
    </div>
  );
};
